import styled, { css } from 'styled-components';
import { screen } from '@/helpers/screen';

//components
import { InvertedSection } from '@/components/Grid';
import {
  FlexColumn,
  FlexRowWrap,
  FlexRowFullCentered,
} from '@/components/Grid/Flex';

//assets
import LoadsmartLogo from '@/public/images/logos/LoadsmartLogo.svg';
import {
  IconSocialFacebook,
  IconSocialInstagram,
  IconSocialYoutube,
  IconSocialTwitter,
  IconSocialLinkedin,
} from '@loadsmart/icons';

//styles
import { DarkFooter } from './Footer.styles';
import {
  LinkSection,
  SocialLinkSection,
  Link,
  IconLink,
  IconGridWrapper,
} from './LinkSection';

const marginFix = css`
  ${screen.md} {
    margin: 20px 16px;
  }

  ${screen.lg} {
    margin: 20px 8px;
  }

  ${screen.xxlg} {
    margin: 0 60px;
  }
`;

const spaceTop = css`
  padding-top: 0px;

  ${screen.md} {
    padding-top: 15px;
  }

  ${screen.lg} {
    padding-top: 20px;
  }

  ${screen.xxlg} {
    padding-top: 40px;
  }
`;

const LogoResponsiveRow = styled(FlexColumn)`
  ${screen.xxlg} {
    flex-direction: row;
  }
`;

const ContentWrapper = styled(FlexRowWrap)`
  margin-top: 20px;

  ${screen.md} {
    margin: 20px -16px;
  }

  ${screen.lg} {
    margin: 20px -8px;
  }

  ${screen.xxlg} {
    margin-top: 0;
    margin-bottom: 60px;
  }
`;

const Copyright = styled(FlexRowFullCentered)`
  font-size: 12px;
  line-height: 14px;

  ${screen.md} {
    font-size: 10px;
  }

  ${screen.xxlg} {
    font-size: 14px;
  }
`;

const FooterLogo = styled(LoadsmartLogo)`
  width: 140px;
`;

const LogoLink = styled.a`
  color: #b2b8bb;

  ${screen.xxlg} {
    margin-right: 40px;
  }
`;

export const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 316px;
  margin: 0 auto;

  ${screen.md} {
    max-width: 674px;
  }

  ${screen.lg} {
    max-width: 980px;
  }

  ${screen.xxlg} {
    max-width: 1380px;
  }
`;

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <DarkFooter>
      <InvertedSection>
        <Container>
          <LogoResponsiveRow>
            <LogoLink href={`/`} aria-label="Loadsmart home page">
              <FooterLogo />
            </LogoLink>
            <ContentWrapper>
              <div>
                <LinkSection css={marginFix} title="shipper">
                  <Link href={`/shipper/`}>Freight Brokerage</Link>
                  <Link href={`/shipper/shipperguide`}>Freight Management</Link>
                  <Link
                    href="https://lp.loadsmart.com/managed-transportation"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Managed Transportation
                  </Link>
                  <Link
                    href="https://lp.loadsmart.com/loadsmart-fleet-optimization"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Private Fleet Optimizer
                  </Link>
                </LinkSection>
                <LinkSection
                  css={`
                    ${marginFix} ${spaceTop};
                  `}
                  title="Warehouses"
                >
                  <Link
                    href="https://opendock.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Dock Scheduling
                  </Link>
                </LinkSection>
              </div>
              <LinkSection css={marginFix} title="carrier">
                <Link href={`/carrier/`}>Carrier Solutions</Link>
                <Link href={`${process.env.CARRIER_URL}/#/`}>
                  Carrier Loadboard
                </Link>
                <Link href="/carrier/tms/">Carrier TMS</Link>
                <Link
                  href="https://lp.loadsmart.com/factoring-sign-up"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Factoring
                </Link>

                <Link
                  href="https://apps.apple.com/us/developer/loadsmart-inc/id983298955"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Apps for iOS
                </Link>
                <Link
                  href="https://play.google.com/store/apps/developer?id=Loadsmart,+Inc.&hl=en_US"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Apps for Android
                </Link>
              </LinkSection>

              <LinkSection css={marginFix} title="About">
                <Link href={`/about`}>Who we are</Link>
                <Link href={`/careers`}>Careers</Link>
                <Link
                  href="https://lp.loadsmart.com/loadsmart-partner-portal-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Partnerships
                </Link>
                <Link href="https://blog.loadsmart.com/">Blog</Link>
                <Link href={`/contact`}>Contact</Link>
              </LinkSection>
              <SocialLinkSection>
                <IconGridWrapper>
                  <IconLink
                    href="https://www.linkedin.com/company/loadsmart"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconSocialLinkedin width={34} height={34} />
                  </IconLink>
                  <IconLink
                    href="https://www.instagram.com/loadsmart"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconSocialInstagram width={34} height={34} />
                  </IconLink>
                  <IconLink
                    href="https://twitter.com/LoadSmartUS/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconSocialTwitter width={34} height={34} />
                  </IconLink>
                  <IconLink
                    href="https://www.youtube.com/channel/UCN-pkKcoMargmyL0Br3-H5w"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconSocialYoutube width={34} height={34} />
                  </IconLink>
                  <IconLink
                    href="https://www.facebook.com/loadsmartUS"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconSocialFacebook width={34} height={34} />
                  </IconLink>
                </IconGridWrapper>
                <Link href={`/user-agreement`}>User Agreement</Link>
                <Link
                  href={`/subscription-and-professional-services-agreement`}
                >
                  Subscription and Professional Services Agreement
                </Link>
                <Link
                  href={`/managed-transportation-master-services-agreement`}
                >
                  Managed Transportation Master Services Agreement
                </Link>
                <Link href={`/privacy-policy`}>Privacy Policy</Link>
              </SocialLinkSection>
            </ContentWrapper>
          </LogoResponsiveRow>
          <Copyright>
            © {currentYear} Loadsmart Inc. All rights reserved.
          </Copyright>
        </Container>
      </InvertedSection>
    </DarkFooter>
  );
};
